<template lang="pug">
div(v-if="followedBusinesses.length > 0")
  .follow-businesses__list#followButton.txt__black.txt__notoSB16(
    ref="scrollContainer"
    class="relative flex items-stretch overflow-x-auto snap-x snap-mandatory scroll-px-[30px]  md:scroll-pl-[30px] gap-4 px-[30px] md:px-0 pb-[10px]"
    @scroll="updateScrollState"
  )
    .follow-businesses__item(
      v-for="(business, index) in followedBusinesses"
      :key="business.id"
      class="relative border p-[30px] rounded-lg flex min-h-[164px] snap-center md:snap-start shrink-0 basis-[200px] sm:basis-[240px]"
      :class="index % 2 === 0 ? 'border-[#749F54]' : 'border-[#E47348]'"
    )
      div(class="w-full flex flex-col")
        .follow-businesses__item--name(
          class="text-center flex-1 flex items-center justify-center"
        )
          span(class="overflow-hidden text-ellipsis line-clamp-3") {{ business['name'] }}
        .follow-businesses__item--btn(class="w-[168px] mx-auto mt-auto flex justify-center pt-4")
          vue-follow-button(
            :is-logged-in="props.isLoggedIn",
            :business-id="business.id",
            mode="mypage",
            @unfollow="handleUnfollow"
          )

  .follow-businesses__scroll-controls(
    v-if="isScrollable"
    class="mt-[18px] flex items-center gap-[30px]"
  )
    .follow-businesses__scrollbar(class="relative h-[4px] flex-1 rounded-full bg-[#E5E5E5] overflow-hidden")
      .follow-businesses__scrollbar-thumb(
        class="absolute left-0 top-0 h-full rounded-full bg-[#888888]"
        :style="{ width: `${scrollThumbWidth}%`, left: `${scrollThumbLeft}%` }"
      )
    .follow-businesses__scroll-buttons(class="flex shrink-0 gap-[14px]")
      button.follow-businesses__scroll-button(
        type="button"
        class="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#A3A3A3] bg-white text-[28px] leading-none disabled:opacity-30"
        :disabled="!canScrollPrev"
        @click="scrollPrev"
        aria-label="前へ"
      )
        span(class="material-symbols-outlined !text-4xl") chevron_left
      button.follow-businesses__scroll-button(
        type="button"
        class="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#A3A3A3] bg-white text-[28px] leading-none disabled:opacity-30"
        :disabled="!canScrollNext"
        @click="scrollNext"
        aria-label="次へ"
      )
        span(class="material-symbols-outlined !text-4xl") chevron_right

div.txt__notoR16.txt__black(
  v-else,
  class="flex items-center justify-center w-full rounded-lg mt-[20px] p-[30px] border border-[#A3A3A3] text-center"
) 現在フォローしている団体はありません。<br>プロジェクトからお気に入りのオーナー (地域団体) を見つけましょう！
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import VueFollowButton from '@/vue/public/VueFollowButton.vue';
import axios from 'axios';

//- フォローしている地域団体の型
interface FollowedBusiness {
  id: number;
  name: string;
};

const baseUrl = (window as any).baseUrl || '';
//- フォローしている地域団体の一覧を入れる箱
const followedBusinesses = ref<FollowedBusiness[]>([]);

//- スクロールする要素をrefで取得
const scrollContainer = ref<HTMLElement | null>(null);
//- スクロール可能かどうか
const isScrollable = ref<boolean>(false);
//- 前へスクロール可能かどうか
const canScrollPrev = ref<boolean>(false);
//- 次へスクロール可能かどうか
const canScrollNext = ref<boolean>(false);
//- スクロールバーの幅
const scrollThumbWidth = ref<number>(100);
//- スクロールバーの位置
const scrollThumbLeft = ref<number>(0);

const props = defineProps<{
  isLoggedIn: boolean;
}>();





onMounted(() => {
  //- マウント時にフォローしている地域団体の一覧を取得
  fetchFollowedBusinesses();
  //- 画面幅が変わったとき、スクロール可能かどうかを再判定
  window.addEventListener('resize', updateScrollState);
});

onUnmounted(() => {
  //- コンポーネントが破棄されたらリサイズイベントを解除
  window.removeEventListener('resize', updateScrollState);
});

const fetchFollowedBusinesses = async () => {
  try {
    const response = await axios.get(`${baseUrl}/business-follow`);
    followedBusinesses.value = response.data.businesses;
    await nextTick();
    updateScrollState();
  } catch (error) {
    console.error(error);
  }
}

//- フォロー解除時の処理
const handleUnfollow = async (businessId: number) => {
  //- フォロー解除した地域団体を配列から削除
  followedBusinesses.value = followedBusinesses.value.filter(business => business.id !== businessId);
  await nextTick();
  updateScrollState();
}

//- スクロール状態を更新
const updateScrollState = () => {
  const scrollContainerElement = scrollContainer.value;
  if (!scrollContainerElement) return;

  //- スクロール対象の中身全体の幅(scrollWidth)から、表示されている幅(clientWidth)を引いた値
  const maxScrollLeft = scrollContainerElement.scrollWidth - scrollContainerElement.clientWidth;

  //- スクロールが必要なコンテンツ量かどうか
  //- カード幅の小数計算などで生じる1px以下の誤差は、スクロール可能と判定しない
  isScrollable.value = maxScrollLeft > 1;
  //- 現在位置が左端より右にあるなら「前へ」ボタンを有効化
  canScrollPrev.value = scrollContainerElement.scrollLeft > 0;
  //- 現在位置が右端に到達していないなら「次へ」ボタンを有効化
  //- -1 はブラウザの小数誤差対策
  canScrollNext.value = scrollContainerElement.scrollLeft < maxScrollLeft - 1;

  if (maxScrollLeft <= 0) {
    scrollThumbWidth.value = 100;
    scrollThumbLeft.value = 0;
    return;
  }


  //- スクロールバーのつまみ幅を計算
  //- 見えている範囲 ÷ 全体の幅
  //- 例: 1000px / 2000px = 50%
  scrollThumbWidth.value = (scrollContainerElement.clientWidth / scrollContainerElement.scrollWidth) * 100;

  //- スクロールバーの位置を計算
  //- 現在のスクロール割合 × バーが移動できる範囲
  //- 例: 50%スクロールしていて、つまみ幅が40%なら
  //-      50% × (100 - 40) = 30%
  scrollThumbLeft.value = (scrollContainerElement.scrollLeft / maxScrollLeft) * (100 - scrollThumbWidth.value);
}

// カード一覧を取得
const getScrollItems = () => {
  const scrollContainerElement = scrollContainer.value;
  if (!scrollContainerElement) return [];

  return Array.from(
    scrollContainerElement.querySelectorAll<HTMLElement>('.follow-businesses__item')
  );
}

// 現在のスクロール位置に一番近いスライドのインデックスを取得する関数
const getCurrentItemIndex = () => {
  const scrollContainerElement = scrollContainer.value;
  if (!scrollContainerElement) return 0;

  const items = getScrollItems();
  if (items.length === 0) return 0;

  const scrollPaddingLeft = 30;
  const currentLeft = scrollContainerElement.scrollLeft + scrollPaddingLeft;

  let currentIndex = 0;
  let shortestDistance = Math.abs(items[0].offsetLeft - currentLeft);

  items.forEach((item, index) => {
    const distance = Math.abs(item.offsetLeft - currentLeft);

    if (distance < shortestDistance) {
      shortestDistance = distance;
      currentIndex = index;
    }
  });

  return currentIndex;
}
// 指定したカードの場所へ移動する
const scrollToItem = (index: number) => {
  const scrollContainerElement = scrollContainer.value;
  if (!scrollContainerElement) return;

  const items = getScrollItems();
  const item = items[index];
  if (!item) return;

  const scrollPaddingLeft = 30;

  scrollContainerElement.scrollTo({
    left: item.offsetLeft - scrollPaddingLeft,
    behavior: 'smooth',
  });
}

// 次のカードに移動する
const scrollNext = () => {
  const items = getScrollItems();
  const currentIndex = getCurrentItemIndex();

  scrollToItem(Math.min(currentIndex + 1, items.length - 1));
}

// 前のカードに移動する
const scrollPrev = () => {
  const currentIndex = getCurrentItemIndex();

  scrollToItem(Math.max(currentIndex - 1, 0));
}
</script>

<style lang="sass" scoped>
</style>
